import { APP_COLORS, createGrantTotalPercent } from "./helpers";
import { translations } from "./lang";

function aggregate(data, groupKeys, fields) {
  const result = {};
  data.forEach((item) => {
    const key = groupKeys.map((k) => item[k] ?? "Unknown").join("|");
    if (!result[key]) {
      result[key] = {};
      fields.forEach((f) => {
        result[key][f] = 0;
      });
    }
    fields.forEach((f) => {
      result[key][f] += item[f] || 0;
    });
  });
  return result;
}
const COLORS = [
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
  "#24ebac",
  "#ebac24",
  "#24c6eb",
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
];
const getAppColor = (app, index) =>
  APP_COLORS[app] || COLORS[index % COLORS.length];
const maps = (lang) => ({
  app: translations[lang].filter.apps,
});
export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export function getTopAndLeastBranches(filteredData = [], topN = 6) {
  if (!filteredData?.length) {
    return {
      topBranches: [],
      leastBranches: [],
    };
  }

  // 1️⃣ Aggregate by branch
  const branchTotals = filteredData.reduce((acc, item) => {
    const branch = item.branch?.trim() || "Unknown";
    const amount = Number(item.completedAmount) || 0;
    const amount2 = Number(item.completed) || 0;

    if (!acc[branch]) {
      acc[branch] = { completedAmount: 0, completed: 0, avg: 0 };
    }
    acc[branch].completedAmount += amount;
    acc[branch].completed += amount2;
    acc[branch].avg =
      Number(acc[branch].completed) > 0
        ? Number(acc[branch].completedAmount) / Number(acc[branch].completed) ||
          0
        : 0;

    return acc;
  }, {});

  const sorted = Object.entries(branchTotals).sort(
    (a, b) => b[1].completedAmount - a[1].completedAmount,
  );

  // 2️⃣ Top N branches
  const topBranches = sorted.slice(0, topN).map(([branch, data]) => ({
    label: branch,
    value: data.completedAmount,
    value2: data.completed,
    value3: data.avg,
  }));

  // 3️⃣ Least N branches (but excluding zeros if you want)
  const leastBranches = sorted
    .slice(-topN)
    .reverse()
    .map(([branch, data]) => ({
      label: branch,
      value: data.completedAmount,
      value2: data.completed,
      value3: data.avg,
    }));

  return {
    topBranches,
    leastBranches,
  };
}

export function preparePieCompletedByApp(data, lang = "ar") {
  const amountCountAvgByAppMonthly = [];
  if (!data?.length) {
    return {
      appPieData: {
        completedAmount: [],
        completed: [],
        avg: [],
      },
      totalCompleted: 0,
      amountCountAvgByAppMonthly,
    };
  }

  const grouped = aggregate(data, ["app"], ["completedAmount", "completed"]);

  const appPieData = {
    completedAmount: [],
    completed: [],
    avg: [],
  };

  let totalCompleted = 0;

  Object.entries(grouped).forEach(
    ([app, { completedAmount = 0, completed = 0 }], i) => {
      totalCompleted += completed;

      const label =
        maps(lang)["app"]?.[app] ??
        translations[lang].notApp + " " + app[app.length - 1];

      const color = getAppColor(app, i);

      // ---- Pie data (unchanged) ----
      appPieData.completedAmount.push({
        id: i,
        label,
        value: completedAmount,
        color,
      });

      appPieData.completed.push({
        id: i,
        label,
        value: completed,
        color,
      });

      appPieData.avg.push({
        id: i,
        label,
        value: completed ? completedAmount / completed : 0,
        color,
      });

      // ---- Flat data for AmountCountAvg ----
      amountCountAvgByAppMonthly.push({
        label,
        value: completedAmount, // Amount
        value2: completed, // Count
        value3: completed ? completedAmount / completed : 0, // Avg
      });
    },
  );

  return { appPieData, totalCompleted, amountCountAvgByAppMonthly };
}

export function prepareMainLineSeries(data, lang = "ar") {
  // Initialize totals per day
  const totalAmountPerDay = {};
  const totalCountPerDay = {};

  DAYS.forEach((d) => {
    totalAmountPerDay[d] = 0;
    totalCountPerDay[d] = 0;
  });

  // Aggregate all data by day
  data.forEach((item) => {
    const dayStr = item.date.split("-")[0]; // assuming 'DD-MM-YYYY'
    const day = parseInt(dayStr, 10);
    if (DAYS.includes(day)) {
      totalAmountPerDay[day] += item.completedAmount || 0;
      totalCountPerDay[day] += item.completed || 0;
    }
  });

  const barSeries = [
    {
      id: "main-amount",
      label: translations[lang].dailyAnalysis.amount || "Amount",
      data: DAYS.map((d) => totalAmountPerDay[d]),
      color: COLORS[0],
      type: "bar",
    },
  ];

  const lineSeries = [
    {
      id: "main-count",
      label: translations[lang].dailyAnalysis.count || "Count",
      data: DAYS.map((d) => totalCountPerDay[d]),
      color: "#003462",
      type: "line",
    },
  ];

  return { barSeries, lineSeries };
}

export function summarizeMonthlyData(data) {
  const result = {};

  data.forEach((item) => {
    const [monthRaw, yearRaw] = item.date.split("-");
    const month = monthRaw.toLowerCase();
    const year = yearRaw;

    if (!result[year]) result[year] = {};
    if (!result[year][month]) {
      // initialize accumulators for this month
      result[year][month] = {
        // raw accumulators
        _completed: 0,
        _completedAmount: 0,
        _deliveryOffer: 0,
        _grandTotal: 0,
        _vat: 0,
        _rejected: 0,
        _rejectedAmount: 0,
        _canceled: 0,
        _canceledAmount: 0,
        _endurance: 0,
        _enduranceAmount: 0,
        _refund: 0,
        _refundAmount: 0,
      };
    }

    const m = result[year][month];

    // accumulate numeric fields (use 0 default)
    m._completed += Number(item.completed || 0);
    m._completedAmount += Number(item.completedAmount || 0);

    m._deliveryOffer += Number(item.offers || 0)+Number(item.paidCampains || 0)+Number(item.deliveryOffer || 0);

    m._grandTotal +=
      Number(item.grandTotal || 0) - Number(item.paidCampains || 0);
    m._vat += Number(item.vat || 0);

    m._rejected += Number(item.rejected || 0);
    m._rejectedAmount += Number(item.rejectedAmount || 0);

    m._canceled += Number(item.canceled || 0);
    m._canceledAmount += Number(item.canceledAmount || 0);

    m._endurance += Number(item.endurance || 0);
    m._enduranceAmount += Number(item.enduranceAmount || 0);

    m._refund += Number(item.refund || 0);
    m._refundAmount += Number(item.refundAmount || 0);
  });

  // Convert accumulators into final shape and compute percents
  Object.keys(result).forEach((year) => {
    Object.keys(result[year]).forEach((month) => {
      const acc = result[year][month];

      const completed = acc._completed;
      const completedAmount = acc._completedAmount;

      const deliveryOffer = acc._deliveryOffer;

      const grandTotal = acc._grandTotal;

      const campaignPercent =
        completedAmount > 0 && deliveryOffer > 0
          ? (deliveryOffer / completedAmount) * 100
          : 0;

      const grandTotalPercent = createGrantTotalPercent({
        completedAmount,
        grandTotal,
      });

      // round percents to 2 decimals for cleaner output
      const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

      // replace accumulator object with final metric structure
      result[year][month] = {
        completed: {
          count: completed,
          amount: completedAmount,
        },
        campaign: {
          percent: round2(campaignPercent),
          amount: deliveryOffer,
        },

        rejected: {
          count: acc._rejected,
          amount: acc._rejectedAmount,
        },

        canceled: {
          count: acc._canceled,
          amount: acc._canceledAmount,
        },

        endurance: {
          count: acc._endurance,
          amount: acc._enduranceAmount,
        },

        refund: {
          count: acc._refund,
          amount: acc._refundAmount,
        },

        grandTotal: {
          percent: grandTotalPercent,
          amount: grandTotal,
        },
      };
    });
  });

  return result;
}

export function summarizeDailyToMonthly(data) {
  const result = {};

  data.forEach((item) => {
    // date format: "31-October-2025"
    const [, monthRaw, year] = item.date.split("-");
    const month = monthRaw.toLowerCase();

    if (!result[year]) result[year] = {};
    if (!result[year][month]) {
      // initialize accumulators
      result[year][month] = {
        _completed: 0,
        _completedAmount: 0,
        _rejected: 0,
        _rejectedAmount: 0,
        _canceled: 0,
        _canceledAmount: 0,
        _endurance: 0,
        _enduranceAmount: 0,
        _refund: 0,
        _refundAmount: 0,
      };
    }

    const m = result[year][month];

    // accumulate daily values
    m._completed += Number(item.completed || 0);
    m._completedAmount += Number(item.completedAmount || 0);

    m._rejected += Number(item.rejected || 0);
    m._rejectedAmount += Number(item.rejectedAmount || 0);

    m._canceled += Number(item.canceled || 0);
    m._canceledAmount += Number(item.canceledAmount || 0);

    m._endurance += Number(item.endurance || 0);
    m._enduranceAmount += Number(item.enduranceAmount || 0);

    m._refund += Number(item.refund || 0);
    m._refundAmount += Number(item.refundAmount || 0);
  });

  // convert accumulators into final structure
  Object.keys(result).forEach((year) => {
    Object.keys(result[year]).forEach((month) => {
      const acc = result[year][month];

      result[year][month] = {
        completed: {
          count: acc._completed,
          amount: acc._completedAmount,
        },
        rejected: {
          count: acc._rejected,
          amount: acc._rejectedAmount,
        },
        canceled: {
          count: acc._canceled,
          amount: acc._canceledAmount,
        },
        endurance: {
          count: acc._endurance,
          amount: acc._enduranceAmount,
        },
        refund: {
          count: acc._refund,
          amount: acc._refundAmount,
        },
      };
    });
  });

  return result;
}

export const ticketMessagesGroupByMonthDays = (
  data,
  targetDate = new Date(),
) => {
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const names = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // initialize with null
  const total = new Array(daysInMonth).fill(null);

  const newData = new Array(daysInMonth).fill(null);
  const inProgressData = new Array(daysInMonth).fill(null);
  const completedData = new Array(daysInMonth).fill(null);
  const rejectedData = new Array(daysInMonth).fill(null);
  const incompleteDataData = new Array(daysInMonth).fill(null);
  const pendingData = new Array(daysInMonth).fill(null);
  const supportData = new Array(daysInMonth).fill(null);

  data.forEach((item) => {
    if (!item.createdAt) return;

    const date = new Date(item.createdAt);

    if (date.getFullYear() !== year || date.getMonth() !== month) {
      return;
    }

    const dayIndex = date.getDate() - 1;

    // total
    if (
      !["team", "client", "app", "manager", "integration",null].includes(item.status)
    ) {
      total[dayIndex] = (total[dayIndex] || 0) + 1;
    }
    // statuses
    if (item.status === "new") {
      newData[dayIndex] = (newData[dayIndex] || 0) + 1;
    } else if (item.status === "inProgress") {
      inProgressData[dayIndex] = (inProgressData[dayIndex] || 0) + 1;
    } else if (item.status === "completed") {
      completedData[dayIndex] = (completedData[dayIndex] || 0) + 1;
    } else if (item.status === "rejected") {
      rejectedData[dayIndex] = (rejectedData[dayIndex] || 0) + 1;
    } else if (item.status === "incompleteData") {
      incompleteDataData[dayIndex] = (incompleteDataData[dayIndex] || 0) + 1;
    } else if (item.status === "pending") {
      pendingData[dayIndex] = (pendingData[dayIndex] || 0) + 1;
    } else if (item.status === "support") {
      supportData[dayIndex] = (supportData[dayIndex] || 0) + 1;
    }
  });

  return {
    names,

    totalData: total,

    newData,
    inProgressData,
    completedData,
    rejectedData,
    incompleteDataData,
    pendingData,
    supportData,
  };
};

// Same output and the same counting rules as ticketMessagesGroupByMonthDays, from the server's per-day counts
// ([{ day, status, count }] of one month, already grouped in the viewer's time zone) instead of every message.
const TICKET_STAGE_STATUSES = ["team", "client", "app", "manager", "integration"];
const TICKET_STATUS_SERIES = {
  new: "newData",
  inProgress: "inProgressData",
  completed: "completedData",
  rejected: "rejectedData",
  incompleteData: "incompleteDataData",
  pending: "pendingData",
  support: "supportData",
};

export const ticketSummaryGroupByDays = (counts = [], targetDate = new Date()) => {
  const daysInMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
  const series = { totalData: new Array(daysInMonth).fill(null) };
  Object.values(TICKET_STATUS_SERIES).forEach((key) => {
    series[key] = new Array(daysInMonth).fill(null);
  });

  counts.forEach(({ day, status, count }) => {
    const index = Number(day) - 1;
    if (!Number.isInteger(index) || index < 0 || index >= daysInMonth || !(count > 0)) return;
    // Stage changes are not counted in the total; a message without a status is (as before).
    if (!TICKET_STAGE_STATUSES.includes(status)) series.totalData[index] = (series.totalData[index] || 0) + count;
    const key = TICKET_STATUS_SERIES[status];
    if (key) series[key][index] = (series[key][index] || 0) + count;
  });

  return { names: Array.from({ length: daysInMonth }, (_, i) => i + 1), ...series };
};

